/* RESPONSIBLE TEAM: team-app-security */
import type IntlService from 'ember-intl/services/intl';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type {
  ScimGroup,
  ScimUser,
  Role,
  DropdownGroup,
  TableColumn,
  SeatType,
  CopilotSeat,
} from './types';
import { ScimGroupState } from './types';
import { SCIM_TRANSLATION_KEYS, SEAT_TYPE_OPTIONS, COPILOT_SEAT_OPTIONS } from './constants';
import {
  getRoleNameById,
  createRoleDropdownList as createSharedRoleDropdownList,
  createRoleSelectGroupList as createSharedRoleSelectGroupList,
  type SelectGroup,
} from 'embercom/utils/teammates/role-dropdown-utils';

export const SCIM_TABLE_COLUMNS = (
  intl: IntlService,
  canSelectSeatType = false,
  canSelectCopilotSeat = false,
): TableColumn[] => {
  let columns: TableColumn[] = [
    {
      label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.PRIORITY),
      valuePath: 'priority',
      sortable: false,
      hasTooltipBlock: true,
    },
    {
      label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.GROUP_NAME),
      valuePath: 'name',
      sortable: false,
    },
    {
      label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.STATE),
      valuePath: 'state',
      sortable: false,
      hasTooltipBlock: true,
    },
    {
      label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.ROLE),
      valuePath: 'role',
      sortable: false,
    },
  ];

  if (canSelectSeatType) {
    columns.push({
      label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.SEAT_TYPE),
      valuePath: 'seat-type',
      sortable: false,
    });
  }

  if (canSelectCopilotSeat) {
    columns.push({
      label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.COPILOT_SEAT),
      valuePath: 'copilot-seat',
      sortable: false,
    });
  }

  columns.push(
    {
      label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.USERS),
      valuePath: 'users',
      sortable: false,
    },
    {
      label: '',
      valuePath: 'actions',
      sortable: false,
    },
  );

  return columns;
};

export const CHANGES_REVIEW_TABLE_COLUMNS = (intl: IntlService): TableColumn[] => [
  {
    label: '',
    valuePath: 'selected',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.CHANGE),
    valuePath: 'change',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.MEMBERSHIP),
    valuePath: 'membership',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.FROM),
    valuePath: 'from',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.TO),
    valuePath: 'to',
    sortable: false,
  },
];

export function getRoleName(
  roleId: string | number | null | undefined,
  roles: Role[],
  intl: IntlService,
): string {
  return getRoleNameById(roleId, roles, intl);
}

export function getUserInitials(user: ScimUser): string {
  return (
    (user.name?.givenName?.charAt(0)?.toUpperCase() || '') +
    (user.name?.familyName?.charAt(0)?.toUpperCase() || '')
  );
}

export function createRoleDropdownList(
  roles: Role[],
  intl: IntlService,
  onSelectRole: (roleId: number | null) => void,
): DropdownGroup[] {
  return createSharedRoleDropdownList(roles, intl, onSelectRole);
}

export function createRoleSelectGroupList(
  roles: Role[],
  intl: IntlService,
  unassignableRoles?: Record<string, { isDisabled: boolean; tooltipText: string }>,
): SelectGroup[] {
  return createSharedRoleSelectGroupList(roles, intl, unassignableRoles);
}

export function arraysEqual<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((val, index) => val === b[index]);
}

export function scimPriorityCompare(a: ScimGroup, b: ScimGroup): number {
  let aPriority = a.priority;
  let bPriority = b.priority;

  let aIsNullish = aPriority === null || aPriority === undefined;
  let bIsNullish = bPriority === null || bPriority === undefined;

  if (aIsNullish && bIsNullish) {
    return 0;
  }
  if (aIsNullish) {
    return 1;
  }
  if (bIsNullish) {
    return -1;
  }

  return aPriority - bPriority;
}

export function sortGroupsByOrder(groups: ScimGroup[], order: string[]): ScimGroup[] {
  let validGroups = groups.filter((group) => group && group.id !== undefined);

  if (order.length === 0) {
    return [...validGroups].sort((a, b) => scimPriorityCompare(a, b));
  }

  let groupsMap = new Map(validGroups.map((group) => [group.id, group]));
  return order.map((id) => groupsMap.get(id)).filter(Boolean) as ScimGroup[];
}

export function getGroupStateDisplayName(state: string | null, intl: IntlService): string {
  if (state === ScimGroupState.ACTIVE) {
    return intl.t(SCIM_TRANSLATION_KEYS.TAB.STATE_ACTIVE);
  }

  if (state === ScimGroupState.INACTIVE) {
    return intl.t(SCIM_TRANSLATION_KEYS.TAB.STATE_INACTIVE);
  }

  return intl.t(SCIM_TRANSLATION_KEYS.TAB.STATE_INACTIVE);
}

export { ScimGroupState };

export function getGroupStateColor(state: string | null): 'green' | 'gray' {
  if (state === ScimGroupState.ACTIVE) {
    return 'green';
  }

  return 'gray';
}

export function getSeatTypeDisplayName(seatType: SeatType, intl: IntlService): string {
  let option = SEAT_TYPE_OPTIONS.find((opt) => opt.value === seatType);
  return option ? intl.t(option.labelKey) : intl.t(SEAT_TYPE_OPTIONS[0].labelKey);
}

export function getCopilotSeatDisplayName(copilotSeat: CopilotSeat, intl: IntlService): string {
  let option = COPILOT_SEAT_OPTIONS.find((opt) => opt.value === copilotSeat);
  return option ? intl.t(option.labelKey) : intl.t(COPILOT_SEAT_OPTIONS[0].labelKey);
}

export interface SeatTypeSelectItem {
  value: SeatType;
  text: string;
}

export interface CopilotSeatSelectItem {
  value: CopilotSeat;
  text: string;
  isDisabled?: boolean;
  tooltipText?: string;
  icon?: InterfaceIconName;
  component?: string;
  componentAttrs?: {
    description?: string;
    copilotSeatPrice?: string;
    icon?: InterfaceIconName;
  };
}

export interface SeatSelectGroup {
  items: SeatTypeSelectItem[] | CopilotSeatSelectItem[];
}

export function createSeatTypeSelectGroupList(intl: IntlService): SeatSelectGroup[] {
  let items: SeatTypeSelectItem[] = SEAT_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    text: intl.t(option.labelKey),
  }));

  return [{ items }];
}

export function createCopilotSeatSelectGroupList(intl: IntlService): SeatSelectGroup[] {
  let items: CopilotSeatSelectItem[] = COPILOT_SEAT_OPTIONS.map((option) => ({
    value: option.value,
    text: intl.t(option.labelKey),
  }));

  return [{ items }];
}
